"use client";

import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import {
  getRegionCookie,
  getStickyManualGeoStorage,
} from "@/app/lib/regionUtils";

interface PolicySubsection {
  id: number;
  heading: string;
  content: string;
  order?: number;
}

interface PolicySection {
  id: number;
  heading: string;
  content: string;
  order?: number;
  subsections?: PolicySubsection[];
}

interface Policy {
  id: number;
  title: string;
  lastUpdated: string;
  introduction: string;
  sections: PolicySection[];
}

interface PolicyContentBaseProps {
  apiEndpoint: string;
  defaultTitle?: string;
  initialPolicy?: Policy | null;
}

/**
 * Generate a URL-friendly ID from a heading text
 */
function generateId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Format date to "Month Day, Year" format (e.g., "October 27, 2025")
 */
function formatLastUpdated(dateString: string | null | undefined): string {
  if (!dateString) return "N/A";

  try {
    // Handle different date formats from Strapi
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing fails
    }

    // Format as "Month Day, Year" (e.g., "October 27, 2025")
    return format(date, "MMMM d, yyyy");
  } catch (error) {
    // If formatting fails, return original string
    return dateString;
  }
}

/**
 * Process content to convert HTML to Markdown format if needed
 */
function processContentForMarkdown(content: string): string {
  if (!content) return "";

  let processed = content;

  // Remove script tags
  processed = processed.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    "",
  );

  // Remove style tags
  processed = processed.replace(
    /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
    "",
  );

  // Remove iframe tags
  processed = processed.replace(/<iframe\b[^>]*>.*?<\/iframe>/gi, "");

  // Convert HTML links to markdown format [text](url)
  processed = processed.replace(
    /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*?>(.*?)<\/a>/gi,
    (match, quote, url, text) => {
      const cleanText = text.replace(/<[^>]*>/g, "").trim();
      return `[${cleanText}](${url})`;
    },
  );

  // Convert HTML headings to markdown
  processed = processed.replace(/<h1[^>]*>(.*?)<\/h1>/gi, "# $1\n\n");
  processed = processed.replace(/<h2[^>]*>(.*?)<\/h2>/gi, "## $1\n\n");
  processed = processed.replace(/<h3[^>]*>(.*?)<\/h3>/gi, "### $1\n\n");
  processed = processed.replace(/<h4[^>]*>(.*?)<\/h4>/gi, "#### $1\n\n");

  // Convert bold and italic
  processed = processed.replace(/<strong[^>]*>(.*?)<\/strong>/gi, "**$1**");
  processed = processed.replace(/<b[^>]*>(.*?)<\/b>/gi, "**$1**");
  processed = processed.replace(/<em[^>]*>(.*?)<\/em>/gi, "*$1*");
  processed = processed.replace(/<i[^>]*>(.*?)<\/i>/gi, "*$1*");

  // Convert lists
  processed = processed.replace(/<ul[^>]*>/gi, "\n");
  processed = processed.replace(/<\/ul>/gi, "\n");
  processed = processed.replace(/<ol[^>]*>/gi, "\n");
  processed = processed.replace(/<\/ol>/gi, "\n");
  processed = processed.replace(/<li[^>]*>(.*?)<\/li>/gi, "- $1\n");

  // Convert paragraphs
  processed = processed.replace(/<p[^>]*>(.*?)<\/p>/gi, "$1\n\n");

  // Remove remaining HTML tags
  processed = processed.replace(/<[^>]+>/g, "");

  // Clean up HTML entities
  processed = processed
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  return processed.trim();
}

/**
 * Markdown content renderer component with policy page styling
 */
const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;

  const processedContent = processContentForMarkdown(content);

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: (props) => (
          <p {...props} className="mb-4 text-gray-300 leading-relaxed" />
        ),
        h1: (props) => (
          <h1 {...props} className="text-3xl font-bold mt-8 mb-4 text-white" />
        ),
        h2: (props) => (
          <h2
            {...props}
            className="text-2xl font-bold mt-6 mb-3 text-[#767EF6]"
          />
        ),
        h3: (props) => (
          <h3
            {...props}
            className="text-xl font-semibold mt-5 mb-2 text-white"
          />
        ),
        h4: (props) => (
          <h4
            {...props}
            className="text-lg font-semibold mt-4 mb-2 text-gray-200"
          />
        ),
        ul: (props) => (
          <ul
            {...props}
            className="list-disc ml-6 mb-4 text-gray-300 space-y-2"
          />
        ),
        ol: (props) => (
          <ol
            {...props}
            className="list-decimal ml-6 mb-4 text-gray-300 space-y-2"
          />
        ),
        li: (props) => (
          <li {...props} className="mb-1 text-gray-300 leading-relaxed" />
        ),
        a: ({ href, children, ...props }) => {
          const safeHref =
            href && !/^https?:\/\//i.test(href) ? `https://${href}` : href;
          return (
            <a
              href={safeHref}
              className="text-[#767EF6] hover:text-[#9BA1FF] hover:underline transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          );
        },
        blockquote: (props) => (
          <blockquote
            {...props}
            className="border-l-4 border-[#767EF6] pl-4 italic my-4 text-gray-300 bg-[#1a1a1a] py-2 rounded-r"
          />
        ),
        code: ({ className, children, ...props }: any) => {
          const match = /language-(\w+)/.exec(className || "");
          const isInline = !match && props.node?.parent?.tagName !== "pre";

          if (isInline) {
            return (
              <code
                {...props}
                className="bg-[#1a1a1a] text-[#767EF6] px-1.5 py-0.5 rounded text-sm font-mono"
              >
                {children}
              </code>
            );
          }

          // For code blocks
          return (
            <code
              {...props}
              className="block bg-[#1a1a1a] text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono mb-4 whitespace-pre"
            >
              {children}
            </code>
          );
        },
        pre: (props) => <pre {...props} className="bg-transparent p-0 mb-4" />,
        strong: (props) => (
          <strong {...props} className="font-bold text-white" />
        ),
        em: (props) => <em {...props} className="italic text-gray-200" />,
        hr: (props) => <hr {...props} className="my-6 border-[#333333]" />,
        table: (props) => (
          <div className="overflow-x-auto my-4">
            <table
              {...props}
              className="min-w-full border-collapse border border-[#333333]"
            />
          </div>
        ),
        thead: (props) => <thead {...props} className="bg-[#1a1a1a]" />,
        tbody: (props) => <tbody {...props} />,
        tr: (props) => <tr {...props} className="border-b border-[#333333]" />,
        th: (props) => (
          <th
            {...props}
            className="border border-[#333333] px-4 py-2 text-left font-semibold text-white"
          />
        ),
        td: (props) => (
          <td
            {...props}
            className="border border-[#333333] px-4 py-2 text-gray-300"
          />
        ),
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

export const PolicyContentBase: React.FC<PolicyContentBaseProps> = ({
  apiEndpoint,
  defaultTitle = "Policy",
  initialPolicy = null,
}) => {
  const [policy, setPolicy] = useState<Policy | null>(initialPolicy);
  const [loading, setLoading] = useState(!initialPolicy);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("");
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Fetch policy from API
  useEffect(() => {
    // If we have initialPolicy, we already set it in state. 
    // We only fetch if it's missing or if we need to fetch a specific region policy.
    const fetchPolicy = async () => {
      try {
        const activeRegion = getRegionCookie() ?? getStickyManualGeoStorage();
        
        // Skip fetching if we already have initialPolicy and no specific region is set
        if (initialPolicy && (!activeRegion || activeRegion === "global")) {
          // ensure TOC active section logic still runs
          if (initialPolicy.sections && initialPolicy.sections.length > 0) {
            const firstSectionId = generateId(initialPolicy.sections[0].heading);
            setActiveSection(firstSectionId);
          }
          return;
        }

        setLoading(true);
        const endpoint =
          activeRegion && activeRegion !== "global"
            ? `${apiEndpoint}?locale=${encodeURIComponent(activeRegion)}`
            : apiEndpoint;

        const response = await fetch(endpoint, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
          },
        });

        if (!response.ok) {
          const errorText = await response
            .text()
            .catch(() => "Unable to read error response");
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            // Could not parse error as JSON
          }
          throw new Error(
            `Failed to fetch policy: ${response.status} ${response.statusText}${errorData?.error ? ` - ${errorData.error}` : ""}`,
          );
        }

        const data = await response.json();

        setPolicy(data);

        // Set first section as active by default
        if (data.sections && data.sections.length > 0) {
          const firstSectionId = generateId(data.sections[0].heading);
          setActiveSection(firstSectionId);
        }
      } catch (err: any) {
        setError(err.message || "Failed to load policy");
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, [apiEndpoint]);

  const activeSectionRef = useRef<string>("");

  // Handle page scroll to update active section
  useEffect(() => {
    if (!policy) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 220; // Offset for header + breathing room

      // Find which section is currently in view
      let currentSection = "";
      let minDistance = Infinity;

      for (const sectionId in sectionRefs.current) {
        const element = sectionRefs.current[sectionId];
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          const elementBottom = elementTop + rect.height;

          // Check if element is in viewport
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            const distance = Math.abs(scrollPosition - elementTop);
            if (distance < minDistance) {
              minDistance = distance;
              currentSection = sectionId;
            }
          }
        }
      }

      if (currentSection && currentSection !== activeSectionRef.current) {
        activeSectionRef.current = currentSection;
        setActiveSection(currentSection);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [policy]);

  // Scroll to section when clicking TOC link
  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const topOffset = 160;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const scrollPosition = Math.max(0, elementTop - topOffset);
      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
      activeSectionRef.current = sectionId;
      setActiveSection(sectionId);
    }
  };

  // Generate table of contents
  const generateTOC = () => {
    if (!policy || !policy.sections) return [];

    const toc: Array<{
      id: string;
      heading: string;
      level: number;
      subsections?: Array<{ id: string; heading: string }>;
    }> = [];

    policy.sections.forEach((section) => {
      const sectionId = generateId(section.heading);
      const tocItem: any = {
        id: sectionId,
        heading: section.heading,
        level: 1,
      };

      if (section.subsections && section.subsections.length > 0) {
        tocItem.subsections = section.subsections.map((subsection) => ({
          id: generateId(subsection.heading),
          heading: subsection.heading,
        }));
      }

      toc.push(tocItem);
    });

    return toc;
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] h-[100dvh] bg-[#191919] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#767EF6] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] h-[100dvh] bg-[#191919] text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Error: {error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-[100dvh] h-[100dvh] bg-[#191919] text-white flex items-center justify-center">
        <p className="text-gray-400">No policy content available</p>
      </div>
    );
  }

  const toc = generateTOC();

  return (
    <div className="min-h-[100dvh] bg-[#191919] text-white">
      {/* Header Section */}
      <div className="bg-[#191919] mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <h1 className="text-3xl md:text-[64px] font-bold mb-2 md:mb-10 md:mt-10">
            {policy.title || defaultTitle}
          </h1>
          <p className="text-[#FCFAFA] text-lg md:mb-10">
            Last updated: {formatLastUpdated(policy.lastUpdated)}
          </p>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 border-t-[4px] border-[#272727]">
        <div className="grid grid-cols-1 lg:grid-cols-[18rem_minmax(0,1fr)] gap-6 lg:gap-10 items-start">
          {/* Left Sidebar - Table of Contents */}
          <aside className="hidden lg:block text-left self-start sticky top-24">
            <div
              className="max-h-[calc(100vh-7rem)] overflow-y-auto border-r-[4px] border-[#272727] pr-4"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="py-2">
                <nav className="space-y-2">
                  {toc.map((item) => (
                    <div key={item.id} className="text-left ">
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left py-2 transition-colors ${
                          activeSection === item.id
                            ? "text-[#3541ED]"
                            : "text-gray-300 hover:text-white"
                        }`}
                      >
                        <span className="text-md font-medium">
                          {item.heading}
                        </span>
                      </button>
                      {item.subsections && item.subsections.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {item.subsections.map((subsection) => (
                            <button
                              key={subsection.id}
                              onClick={() => scrollToSection(subsection.id)}
                              className={`w-full text-left py-1.5 transition-colors text-xs ${
                                activeSection === subsection.id
                                  ? "text-[#3541ED]"
                                  : "text-gray-400 hover:text-gray-300"
                              }`}
                            >
                              {subsection.heading}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="min-w-0">
            {/* Introduction */}
            <div className="mb-8">
              <MarkdownContent content={policy.introduction} />
            </div>

            {/* Sections */}
            <div className="space-y-8">
              {policy.sections.map((section, index) => {
                const sectionId = generateId(section.heading);
                return (
                  <section
                    key={section.id}
                    id={sectionId}
                    ref={(el) => {
                      sectionRefs.current[sectionId] = el;
                    }}
                    className="scroll-mt-36"
                  >
                    <h2 className="text-2xl font-bold mb-4 text-[#767EF6]">
                      {index + 1}. {section.heading}
                    </h2>
                    <div className="mb-4">
                      <MarkdownContent content={section.content} />
                    </div>

                    {/* Subsections */}
                    {section.subsections && section.subsections.length > 0 && (
                      <div className="ml-4 space-y-6 mt-6">
                        {section.subsections.map(
                          (subsection, subsectionIndex) => {
                            const subsectionId = generateId(subsection.heading);
                            return (
                              <div
                                key={subsection.id}
                                id={subsectionId}
                                ref={(el) => {
                                  sectionRefs.current[subsectionId] = el;
                                }}
                                className="scroll-mt-36"
                              >
                                <h3 className="text-xl font-semibold mb-3 text-white">
                                  {`${index + 1}.${subsectionIndex + 1} ${subsection.heading}`}
                                </h3>
                                <div>
                                  <MarkdownContent
                                    content={subsection.content}
                                  />
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
